import React, { FC, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useGetProject from "utils/hooks/useGetProject";
import { useRenderExportedNumber, usePrintParcelInvoice, useDownloadPayslip } from "utils/hooks/SalesReport";
import { ProjectIdType } from "types/Project";
import { ExportFileType } from "types/SalesReport";
import { MOCK_ORDER_REPORT_DATA } from "constants/Report";
import { NOTIFICATION_UPDATE } from "constants/Notification";
import ReportFilter from "domain/Report/ReportFilter";
import { notifySuccess, notifyError } from "utils/notify";
import moment from "moment";
import { OrderState, ShippingCalculationType } from "types/Order";
import { ShippingMethodType } from "domain/Report/ReportFilter/types";
import { ROWS_PER_PAGES, INITIAL_FILTER_LISTS, LIMIT_ID_SELECTED } from "./config";
import { formattedOrderReports } from "./helper";
import { OrderReportsDataType } from "./helper/formattedOrderReports";
import { Wrapper } from "./styled";
import { useQueryOrderReports, useSelectedIds, usePaginationState, useHandleEvent } from "./hooks";
import useImportTrackingNumber from "./hooks/useImportTrackingNumber";
import useExportOrders from "./hooks/useExportOrders";
import ReportTable from "./ReportTable";
import ReportPagination from "./ReportPagination";
import Placeholder from "./Placeholder";
import { PARCEL_MENU_OPTIONS } from "../ReportFilter/config";

export type Filter = {
  dateRange?: { start: string; end: string };
  orderState?: OrderState[];
  shippingMethod?: ShippingMethodType[];
  shippingCalculationType?: ShippingCalculationType[];
  deliveryScheduleDateRange?: { start: string; end: string };
  carrierNames?: string[];
};

const ParcelInvoiceReportContainer: FC = () => {
  const { t } = useTranslation();
  const { projectId } = useParams<ProjectIdType>();
  const { selectAllRowIdsInPage, selectRowId, selectedIds } = useSelectedIds({
    warringMessage: t("parcelInvoiceReport.reachLimit", { limit: LIMIT_ID_SELECTED }),
  });

  const defaultValueFilter = {
    orderState: [OrderState.COD_PREPARE_TO_SHIP, OrderState.PREPARE_TO_SHIPPING],
    shippingCalculationType: [
      ShippingCalculationType.LALAMOVE,
      ShippingCalculationType.DISTANCE_BASE_PRICE,
      ShippingCalculationType.FIXED_PRICE,
      ShippingCalculationType.POSTAL_CODE_BASED_PRICE,
    ],
  };
  const [filter, setFilter] = useState<Filter>({
    ...defaultValueFilter,
    dateRange: {
      start: moment().subtract(6, "days").startOf("day").toISOString(),
      end: moment().endOf("day").toISOString(),
    },
  });

  const { page, rowsPerPage, setPage, setRowsPerPage, setSortBy, sortBy } = usePaginationState();

  const { isConsignmentReportEnabled, isAdvancedConsignmentReportEnabled, loading: projectInfoLoading } = useGetProject(
    projectId,
  );
  const isSkipQuery = !isConsignmentReportEnabled;

  const { loading: orderReportsLoading, orderReports, total, refetch } = useQueryOrderReports({
    projectId,
    isSkip: isSkipQuery,
    filter,
    sortBy,
    page,
    rowsPerPage,
  });

  const { importTrackingNumber, loading: isLoadingImportTrackingNumber } = useImportTrackingNumber(projectId, {
    onCompleted: () => {
      refetch();
      notifySuccess(t(NOTIFICATION_UPDATE.SUCCESS));
    },
  });

  const isReportDisabled = !orderReportsLoading && !projectInfoLoading && !isConsignmentReportEnabled;
  const orderReportsData = isReportDisabled ? MOCK_ORDER_REPORT_DATA : formattedOrderReports(projectId, orderReports);

  const exportedNumber = useRenderExportedNumber({ selectedIds, total });
  const { printParcelInvoice, loading: printParcelInvoiceLoading } = usePrintParcelInvoice();
  const { downloadPayslip, isLoading: downloadPayslipLoading, downloadOrderId } = useDownloadPayslip();

  const {
    handleChangeRowsPerPage,
    handleChangeSortBy,
    handleClickPageNo,
    handleClickPrintShipmentDetail,
  } = useHandleEvent({
    isSalesReportEnabled: isConsignmentReportEnabled,
    printParcelInvoice,
    projectId,
    selectedIds,
    setPage,
    setRowsPerPage,
    setSortBy,
  });

  const exportOrdersOption = useMemo(
    () => ({
      onCompleted: () => {
        notifySuccess(t("shippopReport.export.success"));
      },
      onError: () => {
        notifyError(t("Please try again later"));
      },
    }),
    [t],
  );

  const { exportOrders } = useExportOrders(projectId, filter, exportOrdersOption);

  const handleExportReports = (exportFileType: ExportFileType) => {
    if (!projectId) {
      return;
    }

    exportOrders(exportFileType);
  };

  return (
    <Wrapper>
      <ReportFilter
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        setFilterValue={(newFilter: Record<string, any>) => {
          setFilter({ ...defaultValueFilter, ...newFilter });
        }}
        exportLabel={t("shippopReport.export.label")}
        exportedNumber={t("salesReport.exportedNumber.all")}
        printNumber={exportedNumber}
        handleImportReports={importTrackingNumber}
        projectId={projectId}
        onClickPrintParcelInvoice={handleClickPrintShipmentDetail}
        printParcelInvoiceLoading={printParcelInvoiceLoading}
        defaultFilterList={INITIAL_FILTER_LISTS}
        isLoadingImport={isLoadingImportTrackingNumber}
        isDisabledExportButton={!orderReports.length}
        isDisabledAdvanceButtonAndShowChangePackage={!isAdvancedConsignmentReportEnabled}
        isEnablePrintParcelInvoice={isConsignmentReportEnabled}
        handleExportReports={handleExportReports}
        menuOptions={PARCEL_MENU_OPTIONS}
      />

      {
        // render when no orderReports
        !orderReportsLoading && !projectInfoLoading && orderReports.length === 0 && isConsignmentReportEnabled && (
          <Placeholder message={t("salesReport.orderReports.placeholder")} />
        )
      }

      <ReportTable
        downloadOrderId={downloadOrderId}
        downloadPayslipLoading={downloadPayslipLoading}
        isBlurContent={isReportDisabled}
        onChangeSortBy={handleChangeSortBy}
        onClickDownloadPayslip={downloadPayslip}
        onSelectAllRowInPage={selectAllRowIdsInPage}
        onSelectRow={selectRowId}
        orderReports={orderReportsData as OrderReportsDataType[]}
        orderReportsLoading={orderReportsLoading}
        projectId={projectId}
        projectInfoLoading={projectInfoLoading}
        selectedIds={selectedIds}
      />

      {!isReportDisabled && (
        <ReportPagination
          onChangeRowsPerPage={handleChangeRowsPerPage}
          onClickPageNo={handleClickPageNo}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={ROWS_PER_PAGES}
          total={total}
        />
      )}
    </Wrapper>
  );
};

export default ParcelInvoiceReportContainer;
